import React, { Component } from "react";
import { connect } from "react-redux";
import { FormGroup, FormControl } from "react-bootstrap";
import { FiSearch } from "react-icons/fi";
import moment from "moment";
import Moment from "react-moment";
import Loading from "components/Loading";
import Layout from "components/Layout";
import DataTable from "components/DataTable";
import { loginAttemptsService } from "services";
import { showModal, hideModal } from "actions/modalActions";
import Modal from "components/Modal";
import { withTranslation } from "react-i18next";

const PAGE_SIZE = 40;
const SCROLL_TARGET_ID = "login-attempts-infinite-scroll-target";

class LoginAttempts extends Component {
	constructor(props) {
		super(props);

		this.state = {
			searchUsername: "",
			filterFrom: moment().startOf("day").format("YYYY-MM-DDTHH:mm"),
			filterTo: moment().endOf("day").format("YYYY-MM-DDTHH:mm"),
			filterFailedOnly: false,
			accumulatedRows: [],
			listPage: 0,
			hasMore: true,
			rowsLoading: false,
			rowsLoadingMore: false,
			listLoadError: null,
		};

		this.searchTimeout = null;
		this.currentRequest = null;
	}

	componentDidMount() {
		this.syncStateFromUrl();
	}

	componentDidUpdate(prevProps) {
		if (prevProps.location.search !== this.props.location.search) {
			this.syncStateFromUrl();
		}
	}

	componentWillUnmount() {
		if (this.searchTimeout) clearTimeout(this.searchTimeout);
		if (this.currentRequest) this.currentRequest.abort();
	}

	syncStateFromUrl = () => {
		const params = new URLSearchParams(this.props.location.search);

		const searchUsername = params.get("q") || "";
		const filterFrom =
			params.get("from") ||
			moment().startOf("day").format("YYYY-MM-DDTHH:mm");
		const filterTo =
			params.get("to") ||
			moment().endOf("day").format("YYYY-MM-DDTHH:mm");
		const filterFailedOnly = params.get("failed_login_attempt") === "true";

		this.setState(
			{ searchUsername, filterFrom, filterTo, filterFailedOnly },
			() => this.loadList(false)
		);
	};

	updateUrl = (updates) => {
		const currentParams = new URLSearchParams(this.props.location.search);
		const { searchUsername, filterFrom, filterTo, filterFailedOnly } =
			this.state;

		const newState = {
			q: searchUsername,
			from: filterFrom,
			to: filterTo,
			failed_login_attempt: filterFailedOnly,
			...updates,
		};

		if (newState.q) currentParams.set("q", newState.q);
		else currentParams.delete("q");

		if (newState.from) currentParams.set("from", newState.from);
		else currentParams.delete("from");

		if (newState.to) currentParams.set("to", newState.to);
		else currentParams.delete("to");

		if (newState.failed_login_attempt) currentParams.set("failed_login_attempt", "true");
		else currentParams.delete("failed_login_attempt");

		this.props.history.push({
			pathname: this.props.location.pathname,
			search: currentParams.toString(),
		});
	};

	handleChangeSearch = (event) => {
		const { id, value } = event.target;
		this.setState({ [id]: value });

		if (id === "searchUsername") {
			this.debouncedUpdateUrl(value);
		} else {
			const paramKey =
				id === "filterFrom" ? "from" : id === "filterTo" ? "to" : id;
			this.updateUrl({ [paramKey]: value });
		}
	};

	handleChangeFailedOnly = (event) => {
		const checked = event.target.checked;
		this.setState({ filterFailedOnly: checked });
		this.updateUrl({ failed_login_attempt: checked });
	};

	debouncedUpdateUrl = (searchTerm) => {
		if (this.searchTimeout) clearTimeout(this.searchTimeout);
		if (searchTerm.length >= 3 || searchTerm.length === 0) {
			this.searchTimeout = setTimeout(() => {
				this.updateUrl({ q: searchTerm });
			}, 500);
		}
	};

	buildParams(page) {
		const { searchUsername, filterFrom, filterTo, filterFailedOnly } =
			this.state;

		const params = {
			page,
			page_size: PAGE_SIZE,
		};

		if (searchUsername) params.q = searchUsername;
		if (filterFrom)
			params.from = moment(filterFrom).utc().toISOString();
		if (filterTo)
			params.to = moment(filterTo).utc().toISOString();
		if (filterFailedOnly) params.failed_login_attempt = true;

		return params;
	}

	async loadList(append) {
		if (append) {
			if (
				!this.state.hasMore ||
				this.state.rowsLoadingMore ||
				this.state.rowsLoading
			) {
				return;
			}
		} else if (this.currentRequest) {
			this.currentRequest.abort();
		}

		const pageToFetch = append ? this.state.listPage + 1 : 0;

		if (!append) {
			this.setState({
				accumulatedRows: [],
				listPage: 0,
				hasMore: true,
				rowsLoading: true,
				listLoadError: null,
			});
		} else {
			this.setState({ rowsLoadingMore: true });
		}

		const controller = new AbortController();
		if (!append) this.currentRequest = controller;

		try {
			const res = await loginAttemptsService.getAll(
				this.buildParams(pageToFetch),
				controller.signal
			);

			if (!append) this.currentRequest = null;

			if (!res.ok) {
				throw new Error(
					res.errors?.[0]?.description ||
						this.props.t("loginAttempts.loadError")
				);
			}

			const newRows = Array.isArray(res.data) ? res.data : [];
			const totalItems = res.pagination?.total_items ?? 0;

			const accumulated = append
				? [...this.state.accumulatedRows, ...newRows]
				: newRows;

			let hasMore = false;
			if (newRows.length > 0) {
				if (newRows.length < PAGE_SIZE) {
					hasMore = false;
				} else if (totalItems > 0) {
					hasMore = accumulated.length < totalItems;
				} else {
					hasMore = true;
				}
			}

			this.setState({
				accumulatedRows: accumulated,
				listPage: pageToFetch,
				hasMore,
				rowsLoading: false,
				rowsLoadingMore: false,
				listLoadError: null,
			});
		} catch (error) {
			if (error.name === "AbortError") return;

			if (!append) {
				this.currentRequest = null;
				this.setState({
					rowsLoading: false,
					rowsLoadingMore: false,
					listLoadError:
						error.message || this.props.t("loginAttempts.loadError"),
				});
			} else {
				this.setState({ rowsLoadingMore: false, hasMore: false });
			}
		}
	}

	fetchMore = () => this.loadList(true);

	getColumns = () => {
		const { t } = this.props;
		return [
			{
				key: "created",
				header: t("loginAttempts.colCreated"),
				render: (row) => (
					<Moment format="DD/MM/YYYY HH:mm:ss">{row.created}</Moment>
				),
			},
			{
				key: "username",
				header: t("loginAttempts.colUsername"),
				render: (row) => row.username,
			},
			{
				key: "device",
				header: t("loginAttempts.colDevice"),
				className: "td-device",
				render: (row) => (
					<span title={row.device} className="login-attempts-device">
						{row.device}
					</span>
				),
			},
			{
				key: "failed",
				header: t("loginAttempts.colFailed"),
				align: "center",
				render: (row) =>
					row.failed_login_attempt ? (
						<span className="estado estado-no">
							{t("loginAttempts.failed")}
						</span>
					) : (
						<span className="estado estado-si">
							{t("loginAttempts.success")}
						</span>
					),
			},
		];
	};

	getContent = () => {
		const {
			accumulatedRows,
			rowsLoading,
			rowsLoadingMore,
			hasMore,
			listLoadError,
		} = this.state;
		const { t } = this.props;

		if (rowsLoading && accumulatedRows.length === 0) {
			return <Loading />;
		}

		if (listLoadError) {
			return <p className="no-results">{listLoadError}</p>;
		}

		if (accumulatedRows.length === 0 && !rowsLoading) {
			return <p className="no-results">{t("loginAttempts.noResults")}</p>;
		}

		return (
			<DataTable
				className="login-attempts-datatable-scroll"
				scrollContainerId={SCROLL_TARGET_ID}
				scrollContainerClassName="table-responsive login-attempts-infinite-scroll-wrap"
				tableClassName="table-login-attempts table-hover"
				columns={this.getColumns()}
				rows={accumulatedRows}
				rowKey={(row) => row.id}
				emptyMessage={
					rowsLoadingMore
						? t("loginAttempts.loading")
						: t("loginAttempts.noResults")
				}
				infiniteScroll={{
					dataLength: accumulatedRows.length,
					next: this.fetchMore,
					hasMore,
					loader: rowsLoadingMore ? (
						<div className="text-center py-2">
							{t("loginAttempts.loading")}
						</div>
					) : null,
					endMessage:
						accumulatedRows.length > 0 && !hasMore ? (
							<p className="no-more-results text-center">
								{t("loginAttempts.noMore")}
							</p>
						) : null,
					scrollThreshold: 0.9,
					scrollableTarget: SCROLL_TARGET_ID,
				}}
			/>
		);
	};

	render() {
		const { t } = this.props;
		const { searchUsername, filterFrom, filterTo, filterFailedOnly } =
			this.state;

		return (
			<>
				<Layout
					className="page-login-attempts"
					context="settings"
				>
					<div className="heading">
						<h1 className="title">{t("loginAttempts.title")}</h1>

						<div className="heading-filters">
							<FormGroup
								className="form-group form-search"
								controlId="searchUsername"
							>
								<FormControl
									type="text"
									placeholder={t("loginAttempts.searchPlaceholder")}
									value={searchUsername}
									onChange={this.handleChangeSearch}
								/>
								<FiSearch />
								{searchUsername.length > 0 &&
									searchUsername.length < 3 && (
										<div className="search-hint">
											{t("loginAttempts.searchHint")}
										</div>
									)}
							</FormGroup>

							<FormGroup
								className="form-group"
								controlId="filterFrom"
							>
								<FormControl
									type="datetime-local"
									value={filterFrom}
									onChange={this.handleChangeSearch}
								/>
							</FormGroup>

							<FormGroup
								className="form-group"
								controlId="filterTo"
							>
								<FormControl
									type="datetime-local"
									value={filterTo}
									onChange={this.handleChangeSearch}
								/>
							</FormGroup>

							<div className="form-check form-check-inline" style={{ alignSelf: "center" }}>
								<input
									type="checkbox"
									id="filterFailedOnly"
									className="form-check-input"
									checked={filterFailedOnly}
									onChange={this.handleChangeFailedOnly}
								/>
								<label
									className="form-check-label"
									htmlFor="filterFailedOnly"
								>
									{t("loginAttempts.filterFailedOnly")}
								</label>
							</div>
						</div>
					</div>

					{this.getContent()}
				</Layout>

				<Modal hideModal={this.props.hideModal} />
			</>
		);
	}
}

const mapDispatchToProps = (dispatch) => ({
	hideModal: () => dispatch(hideModal()),
	showModal: (modalProps, modalType) => {
		dispatch(showModal({ modalProps, modalType }));
	},
});

export default connect(
	null,
	mapDispatchToProps
)(withTranslation()(LoginAttempts));
